import { lazy, Suspense, useState } from "react";
import { ClipboardList, FileSpreadsheet } from "lucide-react";

import { ImportFileButton } from "./ImportFileButton";
import { useDraftRowCount } from "@/hooks/use-import-draft";
import { Button } from "@/components/ui/button";
import type { LoadedFile } from "@/lib/order-import/detect-columns";
import { cn } from "@/lib/utils";

// The buttons sit on the eagerly loaded dashboard; the dialog (grid, review, Radix
// Select) is only fetched when a dealer opens it.
const OrderImportDialog = lazy(() =>
  import("./OrderImportDialog").then((m) => ({ default: m.OrderImportDialog })),
);

interface Session {
  id: number;
  file?: LoadedFile;
}

/**
 * «Загрузить заказ из Excel» and «Загрузить по артикулу»: the file button reads the file
 * and opens the dialog with its rows; the other opens the table — empty, or the
 * unfinished list («Продолжить загрузку (N)») if the dealer left one.
 */
export function OrderImportActions({ className }: { className?: string }) {
  const [session, setSession] = useState<Session | null>(null);
  const draftRows = useDraftRowCount();

  const open = (file?: LoadedFile) => setSession((prev) => ({ id: (prev?.id ?? 0) + 1, file }));

  return (
    <div className={cn("flex flex-col gap-2 sm:flex-row", className)}>
      <ImportFileButton variant="outline" onLoaded={open}>
        <FileSpreadsheet aria-hidden="true" />
        Загрузить заказ из Excel
      </ImportFileButton>
      <Button type="button" variant="outline" onClick={() => open()}>
        <ClipboardList aria-hidden="true" />
        {draftRows > 0 ? `Продолжить загрузку (${draftRows})` : "Загрузить по артикулу"}
      </Button>
      {session && (
        <Suspense fallback={null}>
          <OrderImportDialog
            key={session.id}
            open
            onOpenChange={(next) => {
              if (!next) setSession(null);
            }}
            initialFile={session.file}
          />
        </Suspense>
      )}
    </div>
  );
}
